// 视频播放筛选 — 复刻 iOS app 多画面前置选择页
// 路由：/m/multi-select/:storeId
// 流程：进入 → 列出在线设备 → 多选 → 观看视频 → /m/multi-view?sns=a,b,c
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Checkbox, Empty, Input, Spin, Typography } from 'antd';
import { LeftOutlined, SearchOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { fetchFastChannels, fastChannelToDevice, type DeviceLite } from '../api/fast-device.js';

const PRIMARY = '#FF7043';
const MAX_PICK = 4;

export default function MultiSelect() {
	const { storeId } = useParams<{ storeId: string }>();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [devices, setDevices] = useState<DeviceLite[] | null>(null);
	const [picked, setPicked] = useState<Set<string>>(new Set());
	const [search, setSearch] = useState('');
	const [searchOpen, setSearchOpen] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!storeId) return;
		setDevices(null);
		setError(null);
		fetchFastChannels(storeId)
			.then((chs) => setDevices(chs.map(fastChannelToDevice)))
			.catch((e) => {
				setError(e instanceof Error ? e.message : String(e));
				setDevices([]);
			});
	}, [storeId]);

	// 仅在线 + 有 sn
	const online = useMemo(() => {
		return (devices ?? []).filter((d) => String(d.status) === '1' && d.sn);
	}, [devices]);

	const visible = useMemo(() => {
		if (!search) return online;
		const q = search.toLowerCase();
		return online.filter((d) =>
			(d.name ?? '').toLowerCase().includes(q) ||
			String(d.sn ?? '').toLowerCase().includes(q),
		);
	}, [online, search]);

	const toggle = (id: string) => {
		setPicked((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else if (next.size >= MAX_PICK) return prev;
			else next.add(id);
			return next;
		});
	};

	const onWatch = () => {
		if (picked.size === 0) return;
		// 把选中的 device_id (auto_id) 传递到多视图页，由它去 fetch 对应 sn
		const ids = [...picked].join(',');
		navigate(`/m/multi-view/${storeId}?ids=${ids}`);
	};

	return (
		<div style={{ background: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: 720, margin: '0 auto' }}>
			{/* Header */}
			<div style={{
				display: 'flex',
				alignItems: 'center',
				padding: '8px 12px',
				borderBottom: '1px solid #f0f0f0',
				gap: 8,
			}}>
				<Button
					type="text"
					icon={<LeftOutlined style={{ color: PRIMARY, fontSize: 18 }} />}
					onClick={() => navigate(-1)}
				/>
				{searchOpen ? (
					<Input
						autoFocus
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						onBlur={() => { if (!search) setSearchOpen(false); }}
						placeholder={t('mselect.search_ph')}
						style={{ flex: 1, borderRadius: 16 }}
						size="small"
					/>
				) : (
					<>
						<Typography.Title level={5} style={{ flex: 1, margin: 0, textAlign: 'center', fontSize: 16 }}>
							{t('mselect.title')}
						</Typography.Title>
						<Button
							type="text"
							icon={<SearchOutlined style={{ color: PRIMARY, fontSize: 18 }} />}
							onClick={() => setSearchOpen(true)}
						/>
					</>
				)}
			</div>

			{/* Counter row */}
			<div style={{
				padding: '12px 16px',
				background: '#f5f5f5',
				color: '#999',
				fontSize: 13,
			}}>
				{t('mselect.counter', {
					online: online.length,
					picked: picked.size,
				})}
			</div>

			{/* Device list */}
			<div style={{ flex: 1, overflow: 'auto' }}>
				{error && <Alert type="warning" showIcon message={error} style={{ margin: 12 }} />}
				{devices === null && (
					<div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
				)}
				{devices && online.length === 0 && (
					<Empty description={t('mselect.no_online')} style={{ marginTop: 60 }} />
				)}
				{visible.map((d) => {
					const id = String(d.device_id);
					const checked = picked.has(id);
					return (
						<div
							key={id}
							onClick={() => toggle(id)}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 12,
								padding: '14px 16px',
								borderBottom: '1px solid #f5f5f5',
								cursor: 'pointer',
							}}
						>
							<Checkbox checked={checked} onChange={() => toggle(id)} onClick={(e) => e.stopPropagation()} />
							<VideoCameraOutlined style={{ fontSize: 20, color: '#666' }} />
							<span style={{ fontSize: 15, flex: 1 }}>
								{d.name || `# ${d.sn?.slice(-6) ?? d.device_id}`}
							</span>
						</div>
					);
				})}
			</div>

			{/* Bottom action */}
			<div style={{
				padding: '12px 24px 24px',
				borderTop: '1px solid #f0f0f0',
				background: '#fff',
			}}>
				<Button
					type="primary"
					block
					size="large"
					disabled={picked.size === 0}
					onClick={onWatch}
					style={{
						height: 48,
						borderRadius: 24,
						background: picked.size === 0 ? '#ddd' : PRIMARY,
						borderColor:  picked.size === 0 ? '#ddd' : PRIMARY,
						fontSize: 16,
						fontWeight: 600,
					}}
				>
					{t('mselect.watch')}
				</Button>
			</div>
		</div>
	);
}
